import React, { useState, useEffect, useRef } from 'react';
import { injectIntl, FormattedMessage, intlShape } from 'react-intl';
import { Field } from 'react-final-form';
import { number, object, func, string, bool } from 'prop-types';
import classNames from 'classnames';
import { Button } from '@landtrustinc/design-system';
import { FieldNumberInput, Tooltip, OutsideClickHandler, ValidationError } from '..';
import { formatMoney, parseMoneyObjString } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';
import ChevronButton from '../ChevronButton/ChevronButton';

import css from './FlexiblePartySizeSelect.module.css';
import { useScrollTooltipIntoView, useScrollBlock, useIsMobile } from '../../hooks';
import { flexiblePricingConfig } from '../../marketplace-custom-config';

const { Money } = sdkTypes;

// Validation function for flexible pricing rules
const validateFlexiblePricing = (pricing, intl) => {
  const { adultHunter, adultNonHunter, youthHunter, youthNonHunter } = pricing;

  // Rule 1: Cannot have only non-hunters - must have at least one hunter
  if (adultHunter === 0 && youthHunter === 0 && (adultNonHunter > 0 || youthNonHunter > 0)) {
    return intl.formatMessage({
      id: 'FlexiblePartySizeSelect.hunterRequired',
      defaultMessage: 'At least one hunter (adult or youth) is required in the party.',
    });
  }

  // Rule 2: If adult hunters is 0 and there are youth hunters, require at least 1 adult non-hunter
  if (adultHunter === 0 && youthHunter > 0 && adultNonHunter === 0) {
    return intl.formatMessage({
      id: 'FlexiblePartySizeSelect.adultSupervisionRequired',
      defaultMessage:
        'When there are no adult hunters, at least one adult non-hunter is required to supervise youth hunters.',
    });
  }

  return null; // No error
};

const FlexiblePartySizeSelectComponent = ({
  intl,
  maxPartySize,
  minPartySize,
  price,
  input,
  packageFlexiblePricing,
  minAdultHunter = 0,
  minAdultNonHunter = 0,
  minYouthHunter = 0,
  minYouthNonHunter = 0,
  readOnlyInput = false,
  label,
  ...props
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [tempFlexiblePricing, setTempFlexiblePricing] = useState(() => {
    // Initialize with existing values from input or defaults, respecting minimums
    const existingFlexiblePricing = input.value?.flexibleGuestSize;
    return {
      adultHunter: minAdultHunter || existingFlexiblePricing?.adultHunter || minPartySize,
      adultNonHunter: minAdultNonHunter || existingFlexiblePricing?.adultNonHunter || 0,
      youthHunter: minYouthHunter || existingFlexiblePricing?.youthHunter || 0,
      youthNonHunter: minYouthNonHunter || existingFlexiblePricing?.youthNonHunter || 0,
    };
  });

  const [buttonWidth, setButtonWidth] = useState(null);
  const buttonRef = useRef(null);
  const { isMobile } = useIsMobile();

  const handleTooltipVisibility = isVisible => {
    setIsTooltipVisible(isVisible);
  };

  const handleOutsideClick = event => {
    // Check if the click was on the trigger button or its children
    if (buttonRef.current && buttonRef.current.contains(event.target)) {
      return;
    }

    // Only close if the tooltip is visible
    if (isTooltipVisible) {
      handleTooltipVisibility(false);
    }
  };

  const handleButtonClick = () => {
    handleTooltipVisibility(!isTooltipVisible);
  };

  // Update tempFlexiblePricing when input value changes
  useEffect(() => {
    const existingFlexiblePricing = input.value?.flexibleGuestSize;
    if (existingFlexiblePricing) {
      const newPricing = {
        adultHunter: minAdultHunter || existingFlexiblePricing.adultHunter || minPartySize,
        adultNonHunter: minAdultNonHunter || existingFlexiblePricing.adultNonHunter || 0,
        youthHunter: minYouthHunter || existingFlexiblePricing.youthHunter || 0,
        youthNonHunter: minYouthNonHunter || existingFlexiblePricing.youthNonHunter || 0,
      };
      setTempFlexiblePricing(newPricing);

      // Validate on initialization
      const error = validateFlexiblePricing(newPricing, intl);
      setValidationError(error);
    }
  }, [
    input.value,
    minAdultHunter,
    minPartySize,
    minAdultNonHunter,
    minYouthHunter,
    minYouthNonHunter,
    intl,
  ]);

  // Measure button width for dynamic tooltip sizing
  useEffect(() => {
    const measureButtonWidth = () => {
      if (buttonRef.current) {
        const { width } = buttonRef.current.getBoundingClientRect();
        setButtonWidth(width);
      }
    };

    // Measure on mount
    measureButtonWidth();

    // Measure on window resize
    window.addEventListener('resize', measureButtonWidth);

    return () => {
      window.removeEventListener('resize', measureButtonWidth);
    };
  }, []);

  // Measure button width when tooltip becomes visible (in case layout changed)
  useEffect(() => {
    if (isTooltipVisible && buttonRef.current) {
      const { width } = buttonRef.current.getBoundingClientRect();
      setButtonWidth(width);
    }
  }, [isTooltipVisible]);

  // Scroll tooltip into view when it becomes visible
  useScrollTooltipIntoView(isTooltipVisible, `.${css.flexiblePartySizeTooltip}`);

  // Block scrolling on mobile when tooltip is shown
  useScrollBlock(isTooltipVisible);

  // Handle case where price might be a Money object, string, or number
  // If it's a string, parse it using parseMoneyObjString
  let priceValue = price;
  if (typeof price === 'string') {
    priceValue = parseMoneyObjString(price);
  }

  const basePrice =
    priceValue && typeof priceValue === 'object' && priceValue.amount
      ? priceValue.amount
      : priceValue || 0;

  const basePriceMoney = new Money(basePrice, config.currency);

  // Calculate discounted price based on discount percentage
  const calculateDiscountedPrice = discount => {
    const discountAmount = (basePrice * discount) / 100;
    const discountedPrice = basePrice - discountAmount;

    return new Money(Math.max(0, discountedPrice), config.currency);
  };

  // Get flexible pricing configuration - merge package pricing with defaults
  const flexiblePricingDefaults = flexiblePricingConfig.defaultValues;
  const packagePricing = packageFlexiblePricing || {};

  // Merge package pricing with defaults (similar to FieldFlexiblePricing.js)
  const adultNonHunterDiscount =
    packagePricing?.adultNonHunter?.discount ?? flexiblePricingDefaults.adultNonHunter.discount;
  const youthHunterDiscount =
    packagePricing?.youthHunter?.discount ?? flexiblePricingDefaults.youthHunter.discount;
  const youthNonHunterDiscount =
    packagePricing?.youthNonHunter?.discount ?? flexiblePricingDefaults.youthNonHunter.discount;

  const adultHunterQuantity = maxPartySize;
  const adultNonHunterQuantity =
    packagePricing?.adultNonHunter?.quantity ?? flexiblePricingDefaults.adultNonHunter.quantity;
  const youthHunterQuantity =
    packagePricing?.youthHunter?.quantity ?? flexiblePricingDefaults.youthHunter.quantity;
  const youthNonHunterQuantity =
    packagePricing?.youthNonHunter?.quantity ?? flexiblePricingDefaults.youthNonHunter.quantity;

  const allHuntersQuantity = adultHunterQuantity + youthHunterQuantity;

  // Calculate total party size and pricing
  const calculateTotalPartySize = flexPricing => {
    return (
      flexPricing.adultHunter +
      flexPricing.adultNonHunter +
      flexPricing.youthHunter +
      flexPricing.youthNonHunter
    );
  };

  const totalPartySize = calculateTotalPartySize(tempFlexiblePricing);

  const handleQuantityChange = (category, value) => {
    const numValue = parseInt(value, 10) || 0;
    const newFlexiblePricing = {
      ...tempFlexiblePricing,
      [category]: Math.max(0, numValue),
    };

    setTempFlexiblePricing(newFlexiblePricing);

    // Validate the new pricing configuration
    const error = validateFlexiblePricing(newFlexiblePricing, intl);
    setValidationError(error);
  };

  const handleDone = () => {
    const totalSize = calculateTotalPartySize(tempFlexiblePricing);

    // Validate before submitting
    const error = validateFlexiblePricing(tempFlexiblePricing, intl);
    if (error) {
      setValidationError(error);
      return; // Don't submit if there's a validation error
    }

    input.onChange({
      partySize: totalSize,
      flexibleGuestSize: tempFlexiblePricing,
    });
    handleTooltipVisibility(false);

    if (props.onChange) {
      props.onChange({
        partySize: totalSize,
        flexibleGuestSize: tempFlexiblePricing,
      });
    }
  };

  const currentDisplayValue = input.value?.partySize || input.value || minPartySize;

  return (
    <>
      <h5 className={css.formFieldHeading}>{label}</h5>

      {isTooltipVisible && <div className={css.tooltipOverlay} />}

      <Tooltip
        rootClassName={css.flexiblePartySizeTooltip}
        onTooltipShown={isVisible => {
          handleTooltipVisibility(isVisible);
        }}
        tooltipShown={isTooltipVisible}
        hideArrow
        hideCloseButtonDesktop
        placement="bottom-end"
        trigger="none"
        tooltip={
          <OutsideClickHandler
            rootClassName={css.outsideHandler}
            onOutsideClick={handleOutsideClick}
          >
            <div
              className={css.pricingContainer}
              style={{
                // On desktop, use dynamic width; on mobile, let CSS handle responsive width
                width: buttonWidth && !isMobile ? `${buttonWidth}px` : undefined,
                minWidth: buttonWidth && !isMobile ? `${buttonWidth}px` : undefined,
              }}
            >
              <div className={css.pricingTiers}>
                {/* Adult Hunter */}
                {adultHunterQuantity > 0 && (
                  <div className={css.pricingTier}>
                    <div className={css.tierInfo}>
                      <div className={css.tierTitle}>
                        <FormattedMessage id="FlexiblePartySizeSelect.adultHunterLabel" />
                      </div>
                      <div className={css.tierSubtitle}>
                        <FormattedMessage
                          id="FlexiblePartySizeSelect.agesLabel"
                          values={{ ages: '16+' }}
                        />
                      </div>
                      <div className={css.tierPrice}>
                        {formatMoney(intl, basePriceMoney)}
                        <span className={css.tierPriceUnit}>
                          <FormattedMessage id="FlexiblePartySizeSelect.perDayLabel" />
                        </span>
                      </div>
                    </div>
                    <div className={css.tierControls}>
                      <FieldNumberInput
                        className={css.quantityInput}
                        value={tempFlexiblePricing.adultHunter}
                        onChange={value => handleQuantityChange('adultHunter', value)}
                        min={minAdultHunter > 0 ? minAdultHunter : minPartySize}
                        max={adultHunterQuantity}
                        placeholder="1"
                        onFocus={() => {}}
                        onBlur={() => {}}
                        readOnlyInput={readOnlyInput}
                      />
                    </div>
                  </div>
                )}

                {/* Adult Non-Hunter */}
                {adultNonHunterQuantity > 0 && (
                  <div className={css.pricingTier}>
                    <div className={css.tierInfo}>
                      <div className={css.tierTitle}>
                        <FormattedMessage id="FlexiblePartySizeSelect.adultNonHunterLabel" />
                      </div>
                      <div className={css.tierSubtitle}>
                        <FormattedMessage
                          id="FlexiblePartySizeSelect.agesLabel"
                          values={{ ages: '16+' }}
                        />
                      </div>
                      <div className={css.tierPrice}>
                        {formatMoney(intl, calculateDiscountedPrice(adultNonHunterDiscount))}
                        <span className={css.tierPriceUnit}>
                          <FormattedMessage id="FlexiblePartySizeSelect.perDayLabel" />
                        </span>
                      </div>
                    </div>
                    <div className={css.tierControls}>
                      <FieldNumberInput
                        className={css.quantityInput}
                        value={tempFlexiblePricing.adultNonHunter}
                        onChange={value => handleQuantityChange('adultNonHunter', value)}
                        min={minAdultNonHunter}
                        max={adultNonHunterQuantity}
                        placeholder="0"
                        onFocus={() => {}}
                        onBlur={() => {}}
                        readOnlyInput={readOnlyInput}
                      />
                    </div>
                  </div>
                )}

                {/* Youth Hunter */}
                {youthHunterQuantity > 0 && (
                  <div className={css.pricingTier}>
                    <div className={css.tierInfo}>
                      <div className={css.tierTitle}>
                        <FormattedMessage id="FlexiblePartySizeSelect.youthHunterLabel" />
                      </div>
                      <div className={css.tierSubtitle}>
                        <FormattedMessage
                          id="FlexiblePartySizeSelect.agesLabel"
                          values={{ ages: '3-15' }}
                        />
                      </div>
                      <div className={css.tierPrice}>
                        {formatMoney(intl, calculateDiscountedPrice(youthHunterDiscount))}
                        <span className={css.tierPriceUnit}>
                          <FormattedMessage id="FlexiblePartySizeSelect.perDayLabel" />
                        </span>
                      </div>
                    </div>
                    <div className={css.tierControls}>
                      <FieldNumberInput
                        className={css.quantityInput}
                        value={tempFlexiblePricing.youthHunter}
                        onChange={value => handleQuantityChange('youthHunter', value)}
                        min={minYouthHunter}
                        max={youthHunterQuantity}
                        placeholder="0"
                        onFocus={() => {}}
                        onBlur={() => {}}
                        readOnlyInput={readOnlyInput}
                      />
                    </div>
                  </div>
                )}

                {/* Youth Non-Hunter */}
                {youthNonHunterQuantity > 0 && (
                  <div className={css.pricingTier}>
                    <div className={css.tierInfo}>
                      <div className={css.tierTitle}>
                        <FormattedMessage id="FlexiblePartySizeSelect.youthNonHunterLabel" />
                      </div>
                      <div className={css.tierSubtitle}>
                        <FormattedMessage
                          id="FlexiblePartySizeSelect.agesLabel"
                          values={{ ages: '3-15' }}
                        />
                      </div>
                      <div className={css.tierPrice}>
                        {youthNonHunterDiscount === 100 ? (
                          <span className={css.freePrice}>
                            <FormattedMessage id="FlexiblePartySizeSelect.freeLabel" />
                          </span>
                        ) : (
                          <>
                            {formatMoney(intl, calculateDiscountedPrice(youthNonHunterDiscount))}
                            <span className={css.tierPriceUnit}>
                              <FormattedMessage id="FlexiblePartySizeSelect.perDayLabel" />
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={css.tierControls}>
                      <FieldNumberInput
                        className={css.quantityInput}
                        value={tempFlexiblePricing.youthNonHunter}
                        onChange={value => handleQuantityChange('youthNonHunter', value)}
                        min={minYouthNonHunter}
                        max={youthNonHunterQuantity}
                        placeholder="0"
                        onFocus={() => {}}
                        onBlur={() => {}}
                        readOnlyInput={readOnlyInput}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className={css.partySizeLabel}>
                <FormattedMessage
                  id="FlexiblePartySizeSelect.totalGuestsLabel"
                  values={{ count: allHuntersQuantity }}
                />
              </div>

              {validationError && (
                <ValidationError
                  fieldMeta={{
                    error: validationError,
                    touched: true,
                  }}
                />
              )}

              <div className={css.dropdownActions}>
                <Button
                  fullWidth
                  variant="primary"
                  onClick={handleDone}
                  disabled={totalPartySize === 0 || validationError}
                >
                  <FormattedMessage id="FlexiblePartySizeSelect.doneLabel" />
                </Button>
              </div>
            </div>
          </OutsideClickHandler>
        }
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: () => {
                return [0, 25];
              },
            },
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['bottom-end'],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 20,
            },
          },
        ]}
      >
        <ChevronButton
          ref={buttonRef}
          testId="party-size-chooser"
          onClick={handleButtonClick}
          className={classNames(css.flexiblePartySizeButton)}
          isExpanded={isTooltipVisible}
        >
          <FormattedMessage
            id="FlexiblePartySizeSelect.guestsLabel"
            values={{ count: currentDisplayValue }}
          />
        </ChevronButton>
      </Tooltip>
    </>
  );
};

const FlexiblePartySizeSelect = ({
  intl,
  name,
  validate,
  packageFlexiblePricing,
  readOnlyInput,
  ...props
}) => {
  return (
    <Field
      name={name}
      validate={validate}
      component={FlexiblePartySizeSelectComponent}
      packageFlexiblePricing={packageFlexiblePricing}
      readOnlyInput={readOnlyInput}
      intl={intl}
      {...props}
    />
  );
};

FlexiblePartySizeSelect.defaultProps = {
  price: null,
  validate: undefined,
  packageFlexiblePricing: null,
  activity: null,
  readOnlyInput: false,
};

FlexiblePartySizeSelect.propTypes = {
  intl: intlShape.isRequired,
  name: string.isRequired,
  maxPartySize: number.isRequired,
  minPartySize: number.isRequired,
  price: object,
  validate: func,
  packageFlexiblePricing: object,
  activity: string,
  readOnlyInput: bool,
};

export default injectIntl(FlexiblePartySizeSelect);
